import { Box, Flex, Heading, useTheme, VStack } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { AnimatedUnderline } from '../../elements/AnimatedUnderline/AnimatedUnderline'
import { HighlightFirstWord } from '../../elements/HighlightFirstWord/HighlightFirstWord'
import { NextLink } from '@stocker/ui-components/design-system'
import type { IImage } from '@stocker/ui-components/design-system'
import NextImage from 'next/image'
import type React from 'react'
import type { ReactNode } from 'react'

export interface IProductLinesProps {
  image: IImage
  productLine?: string
  title: string
  text: ReactNode
  link: {
    absolutePath: string | null
  }
  linkText: string
}

interface IColors {
  colorBackground?: string
  colorBackgroundBox?: string
  colorText?: string
}
interface ICombined extends IColors, IProductLinesProps {}

export const ProductLineCard: React.FC<ICombined> = ({
  productLine, title, text, link, image, linkText,
  colorBackground = 'secondaryBackground.500',
  colorBackgroundBox = 'secondaryBackground.550',
  colorText = 'secondaryText.900',
}) => {
  const theme = useTheme()
  return (
    <Box
      w="100%"
      h="100%"
      position="relative"
      bgColor={colorBackground}
      color={colorText}
    >
      <Box
        bgColor={colorBackgroundBox}
        maxW="700px"
        _after={{
          // trick to make it square
          content: "''",
          display: 'block',
          paddingBottom: '100%',
        }}
        position="relative"
        overflow="hidden"
        p="15px"
        transition="transform 0.3s ease, box-shadow 0.3s ease"
        _hover={{
          transform: 'scale(1.02)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Flex
          position="absolute"
          height="calc(100% - 30px)"
          width="50%"
          justify="center"
          align="center"
          top="15px"
          left="15px"
        >
          <Box w="100%" h="100%" position="relative">
            <NextImage
              src={image.fullpath ?? ''}
              placeholder={image.blurDataUrl ? 'blur' : 'empty'}
              blurDataURL={image.blurDataUrl}
              fill
              sizes="100vw"
              style={{
                objectFit: 'contain',
              }}
              alt={title}
            />
          </Box>
        </Flex>
        <VStack
          position="absolute"
          top="15px"
          left="50%"
          right="15px"
          bottom="15px"
          alignItems="flex-start"
          justify="center"
          overflow="hidden"
          pl="20px"
        >
        <Box>
          <HighlightFirstWord
            fontFamily="heading"
            productLine={productLine ?? ''}
            as="h6"
          />
        </Box>
        <Heading
          as="h4"
          position="relative"
          fontSize="xl"
          _before={{
            position: 'absolute',
            bgColor: 'black',
            content: "''",
            left: '-60px',
            width: '50px',
            height: '1px',
            top: '50%',
          }}
        >{title}
        </Heading>
        <Box
          w="90%"
          css={css`
            @media (max-width: ${theme.breakpoints.lg}) {
              display: none;
            }
          `}
        >
          <Box
            fontSize="md"
            fontWeight={400}
            pb={3}
          >
            {text}
          </Box>
        </Box>
        <Box>
          {link.absolutePath && (
            <NextLink
              href={link.absolutePath}
            >
              <AnimatedUnderline
                fontSize="lg"
                display="inline-block"
              >
                {linkText}
              </AnimatedUnderline>
            </NextLink>
          )}
        </Box>
        </VStack>
      </Box>
    </Box>
  )
}
